import clsx from 'clsx';
import { format } from 'date-fns';
import { func, oneOf } from 'prop-types';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RadioGroup } from 'components';
import { addUuid } from 'helpers';

import imgCards from 'assets/img/card-icons.svg';
import imgPlaceholder from 'assets/img/img-placeholder.png';

const PaymentOption = ({ type }) => {
  const { t } = useTranslation();

  return (
    <div>
      <div className="flex items-center gap-4 mb-2">
        <span className="text-sm uppercase font-bold mt-1">{t(`auth:registration.steps.subscription.${type}`)}</span>
        {type === 'creditCard' && (
          <img src={imgCards} alt={t('auth:registration.steps.creditCardAlt')} width="72" height="34" />
        )}
      </div>
      <p className="text-gray-600">{t(`auth:registration.steps.subscription.${type}PaymentDescription`)}</p>
    </div>
  );
};

const MembershipSubscriptionStep = ({ changeStep }) => {
  const { t } = useTranslation();
  const {
    formState: { isValid },
    submitClasses,
    getValues
  } = useFormContext();
  const year = useMemo(() => format(new Date(), 'yy'), []);
  const paymentOptions = useMemo(
    () =>
      addUuid(
        [
          {
            name: 'paymentMethod',
            value: 'credit-card',
            content: () => <PaymentOption type="creditCard" />
          },
          {
            name: 'paymentMethod',
            value: 'cheque',
            content: () => <PaymentOption type="cheque" />
          }
        ],
        'payment-method-radio-'
      ),
    []
  );

  return (
    <div className="subscription-step">
      <div className="flex gap-2 mb-4 md:gap-6">
        <div className="rounded-lg overflow-hidden w-[7.5rem] h-[7.5rem]">
          <img src={imgPlaceholder} alt="" width="120" height="120" className="w-full h-full object-cover" />
        </div>

        <div className="flex flex-col gap-6 grow">
          <p className="uppercase font-bold">{t('auth:registration.steps.subscription.title')}</p>
          <p className="text-gray-600">
            {t('auth:registration.steps.subscription.available', { date: `31/12/${year}` })}
          </p>
          <p className="flex-center-between">
            {t('auth:registration.steps.subscription.total')}
            <span className="font-bold">15,00€</span>
          </p>
        </div>
      </div>

      <h2 className="h2 underlined-blue mt-6">{t('auth:registration.steps.payment')}</h2>

      <RadioGroup radioOptions={paymentOptions} rules={{ required: true }} />

      {getValues('paymentMethod') === 'credit-card' ? (
        <a
          href={`${process.env.REACT_APP_CASTELIS_TUNNEL_URL}/index/account?login=${getValues('userId')}`}
          className={clsx('uppercase mx-auto w-fit', submitClasses)}
        >
          {t('form:submit')}
        </a>
      ) : (
        <button
          type="button"
          disabled={!isValid}
          onClick={changeStep}
          className={clsx('uppercase mx-auto w-fit', submitClasses)}
          data-test-id="membershipSubscriptionSubmitBtn"
        >
          {t('form:submit')}
        </button>
      )}
    </div>
  );
};

PaymentOption.propTypes = {
  type: oneOf(['creditCard', 'cheque', 'cash'])
};

MembershipSubscriptionStep.propTypes = {
  changeStep: func.isRequired
};

export default MembershipSubscriptionStep;
